import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Eye, Edit, Coins } from 'lucide-react';
import api from '../../lib/api';

interface Asset {
  id: string;
  name: string;
  category: string;
  status: string;
  totalValue: number;
  tokenSymbol: string;
  currentSupply: number;
  maxSupply: number | null;
  unitPrice: number;
  createdAt: string;
  submittedAt: string | null;
  approvedAt: string | null;
}

const statusColors: Record<string, string> = {
  DRAFT: 'bg-surface-600 text-surface-200',
  PENDING_APPROVAL: 'bg-yellow-500/20 text-yellow-400',
  APPROVED: 'bg-green-500/20 text-green-400',
  REJECTED: 'bg-red-500/20 text-red-400',
  TOKENIZED: 'bg-primary-500/20 text-primary-400',
  SUSPENDED: 'bg-orange-500/20 text-orange-400',
};

const statusLabels: Record<string, string> = {
  DRAFT: 'Rascunho',
  PENDING_APPROVAL: 'Aguardando Aprovação',
  APPROVED: 'Aprovado',
  REJECTED: 'Rejeitado',
  TOKENIZED: 'Tokenizado',
  SUSPENDED: 'Suspenso',
};

const categoryLabels: Record<string, string> = {
  STABLECOIN: 'Stablecoin',
  RECEIVABLE: 'Recebíveis',
  REAL_ESTATE: 'Imóveis',
  FUND_SHARE: 'Cotas de Fundo',
  FIXED_INCOME: 'Renda Fixa',
  COMMODITY: 'Commodities',
  PRECATORY: 'Precatórios',
};

export function AssetList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const { data: assets, isLoading } = useQuery({
    queryKey: ['assets', search, statusFilter, categoryFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      const res = await api.get(`/tokenization/assets?${params}`);
      return res.data as Asset[];
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Meus Ativos</h1>
          <p className="text-surface-400">Gerencie seus ativos tokenizáveis</p>
        </div>
        <button
          onClick={() => navigate('/admin/tokenization/assets/new')}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
        >
          <Plus className="w-5 h-5" />
          Novo Ativo
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar ativos..."
              className="w-full pl-10 pr-4 py-2 bg-surface-800 border border-surface-700 rounded-lg"
            />
          </div>
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg"
        >
          <option value="">Todos os Status</option>
          <option value="DRAFT">Rascunho</option>
          <option value="PENDING_APPROVAL">Aguardando Aprovação</option>
          <option value="APPROVED">Aprovado</option>
          <option value="REJECTED">Rejeitado</option>
          <option value="TOKENIZED">Tokenizado</option>
        </select>

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg"
        >
          <option value="">Todas as Categorias</option>
          {Object.entries(categoryLabels).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      {/* Assets Table */}
      <div className="bg-surface-900 rounded-xl border border-surface-800 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-surface-400">Carregando ativos...</p>
          </div>
        ) : !assets?.length ? (
          <div className="p-8 text-center">
            <Coins className="w-12 h-12 text-surface-600 mx-auto mb-3" />
            <p className="text-surface-400 mb-4">Nenhum ativo encontrado</p>
            <button
              onClick={() => navigate('/admin/tokenization/assets/new')}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
            >
              Criar Primeiro Ativo
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-surface-800">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-400">Ativo</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-400">Categoria</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-400">Status</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-surface-400">Valor Total</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-surface-400">Supply</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-surface-400">Preço Unit.</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-surface-400">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-800">
              {assets.map((asset) => (
                <tr key={asset.id} className="hover:bg-surface-800/50 transition-colors">
                  <td className="px-4 py-4">
                    <div>
                      <p className="font-medium">{asset.name}</p>
                      <p className="text-sm text-surface-400">{asset.tokenSymbol}</p>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm">{categoryLabels[asset.category] || asset.category}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[asset.status]}`}>
                      {statusLabels[asset.status] || asset.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    {formatCurrency(asset.totalValue)}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span>{formatNumber(asset.currentSupply)}</span>
                    {asset.maxSupply && (
                      <span className="text-surface-400"> / {formatNumber(asset.maxSupply)}</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-right">
                    {formatCurrency(asset.unitPrice)}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => navigate(`/admin/tokenization/assets/${asset.id}`)}
                        className="p-2 hover:bg-surface-700 rounded-lg transition-colors"
                        title="Ver Detalhes"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {asset.status === 'DRAFT' && (
                        <button
                          onClick={() => navigate(`/admin/tokenization/assets/${asset.id}/edit`)}
                          className="p-2 hover:bg-surface-700 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                      {asset.status === 'APPROVED' && (
                        <button
                          onClick={() => navigate(`/admin/tokenization/assets/${asset.id}/token-config`)}
                          className="p-2 hover:bg-surface-700 rounded-lg transition-colors text-primary-400"
                          title="Configurar Token"
                        >
                          <Coins className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default AssetList;
